<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="callbacks"
      :loading="loading"
      item-key="id"
      class="elevation-1"
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Callbacks</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-spacer></v-spacer>
          <v-text-field
            v-model="search"
            append-icon="mdi-magnify"
            label="Search"
            single-line
            hide-details
          ></v-text-field>
        </v-toolbar>
      </template>
      <template v-slot:item.call_date="{ item }">
        {{ formatDate(item.call_date) }}
      </template>
      <template v-slot:item.called_at="{ item }">
        {{ formatDate(item.phone_call ? item.phone_call.call_date : null) }}
      </template>
      <template v-slot:item.business_name="{ item }">
        {{ item.phone_call ? item.phone_call.business_name : '' }}
      </template>
      <template v-slot:item.phone_number="{ item }">
        {{ item.phone_call ? item.phone_call.phone_number : '' }}
      </template>
      <template v-slot:item.notes="{ item }">
        {{ item.phone_call ? item.phone_call.notes : '' }}
      </template>
      <template v-slot:item.actions="{ item }">
        <v-btn color="primary" @click="makePhoneCall(item)" class="mr-2">Make Call</v-btn>
        <v-btn color="secondary" @click="openRescheduleDialog(item)">Reschedule</v-btn>
      </template>
    </v-data-table>

    <v-dialog v-model="rescheduleDialog" max-width="500px">
      <v-card>
        <v-card-title class="headline">Reschedule Callback</v-card-title>
        <v-card-text>
          <v-menu
            ref="rescheduleDateMenu"
            v-model="rescheduleDateMenuVisible"
            :close-on-content-click="false"
            :return-value.sync="rescheduleDate"
            offset-y
            min-width="290px"
          >
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="rescheduleDate"
                label="New Callback Date"
                prepend-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
              ></v-text-field>
            </template>
            <v-date-picker
              v-model="rescheduleDate"
              no-title
              scrollable
            >
              <v-spacer></v-spacer>
              <v-btn text color="primary" @click="rescheduleDateMenuVisible = false">
                Cancel
              </v-btn>
              <v-btn text color="primary" @click="rescheduleCallback">
                OK
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeRescheduleDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { axios } from '../../../utils';

export default {
  props: {
    id: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      callbacks: [],
      loading: false,
      search: '',
      headers: [
        { text: 'To be called at', value: 'call_date' },
        { text: 'Called at', value: 'called_at' },
        { text: 'Business Name', value: 'business_name' },
        { text: 'Phone Number', value: 'phone_number' },
        { text: 'Notes', value: 'notes' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      rescheduleDialog: false,
      selectedCallback: null,
      rescheduleDate: new Date().toISOString().substr(0, 10),
      rescheduleDateMenuVisible: false,
    };
  },
  methods: {
    async fetchCallbacks() {
      this.loading = true;

      try {
        const response = await axios.get('/api/admin/callbacks', {
          params: {
            employee_id: this.id
          }
        });
        this.callbacks = response.data.callbacks;
      } catch (error) {
        console.error("There was an error fetching the callbacks:", error);
      } finally {
        this.loading = false;
      }
    },
    formatDate(date) {
      if (!date) return '-';
      return new Date(date).toLocaleDateString();
    },
    async makePhoneCall(item) {
      if (confirm('Are you sure you want to mark this callback as a phone call?')) {
        this.loading = true;
        try {
          // Delete the callback
          await axios.delete(`/api/admin/callbacks/${item.id}`);
          // Update the call_date of the associated phone call
          if (item.phone_call && item.phone_call.id) {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, '0'); // Month is 0-indexed
            const day = String(today.getDate()).padStart(2, '0');
            const currentDate = `${year}-${month}-${day}`;

            await axios.put(`/api/admin/phone-calls/${item.phone_call.id}`, {
              call_date: currentDate, // Use the locally constructed date
              business_name: item.phone_call.business_name,
              phone_number: item.phone_call.phone_number,
              call_results: item.phone_call.call_results,
              employee_id: this.id,
              // You might need to include other fields as well
            });
            this.$emit('refresh-phone-calls'); // Emit an event to refresh the phone calls component
          }
          this.fetchCallbacks();
        } catch (error) {
          console.error("Error marking callback as phone call:", error);
        } finally {
          this.loading = false;
        }
      }
    },
    openRescheduleDialog(item) {
      this.selectedCallback = item;
      this.rescheduleDate = item.call_date;
      this.rescheduleDialog = true;
    },
    closeRescheduleDialog() {
      this.rescheduleDialog = false;
      this.selectedCallback = null;
    },
    async rescheduleCallback() {
      if (!this.selectedCallback) return;
      this.loading = true;
      try {
        // Update the callback date
        await axios.put(`/api/admin/callbacks/${this.selectedCallback.id}`, {
          call_date: this.rescheduleDate
        });
        // Update the call_date of the associated phone call
        if (this.selectedCallback.phone_call && this.selectedCallback.phone_call.id) {
          await axios.put(`/api/admin/phone-calls/${this.selectedCallback.phone_call.id}`, {
            call_date: this.rescheduleDate,
            business_name: this.selectedCallback.phone_call.business_name,
            phone_number: this.selectedCallback.phone_call.phone_number,
            call_results: this.selectedCallback.phone_call.call_results,
            employee_id: this.id, // Assuming 'this.id' is the employee ID
            // You might need to include other fields as well, depending on your PhoneCall model
          });
          this.$emit('refresh-phone-calls'); // Emit an event to refresh the phone calls component
        }
        this.fetchCallbacks();
        this.closeRescheduleDialog();
      } catch (error) {
        console.error("Error rescheduling callback:", error);
      } finally {
        this.loading = false;
      }
    },
  },
  mounted() {
    this.fetchCallbacks();
    console.log(this.id, 'from callbacks mounted');
  },
};
</script>

<style scoped>
.elevation-1 {
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.12), 0 1px 2px rgba(0, 0, 0, 0.24);
}
</style>